import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  AccountBalance as BankIcon,
  DragIndicator as DragIcon,
  TrendingDown as DebitIcon,
  TrendingUp as CreditIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { BankTransaction } from "../services/matchingService";

interface TransactionCardProps {
  transaction: BankTransaction;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent, transaction: BankTransaction) => void;
  selected?: boolean;
  onClick?: () => void;
}

const TransactionCard: React.FC<TransactionCardProps> = ({
  transaction,
  draggable = false,
  onDragStart,
  selected = false,
  onClick,
}) => {
  const valor = Number(transaction.valor);
  const isDebit = valor < 0;

  return (
    <Card
      draggable={draggable}
      onDragStart={(e) => onDragStart && onDragStart(e, transaction)}
      onClick={onClick}
      sx={{
        cursor: draggable ? "grab" : onClick ? "pointer" : "default",
        border: selected ? "2px solid" : "1px solid",
        borderColor: selected ? "primary.main" : "divider",
        bgcolor: selected ? "action.selected" : "background.paper",
        "&:hover": {
          boxShadow: draggable || onClick ? 3 : 1,
        },
        "&:active": {
          cursor: draggable ? "grabbing" : "default",
        },
      }}
    >
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <BankIcon color={isDebit ? "error" : "success"} />
            <Typography
              variant="h6"
              component="div"
              color={isDebit ? "error.main" : "success.main"}
            >
              €{Math.abs(valor).toFixed(2)}
            </Typography>
          </Box>
          {draggable && (
            <Tooltip title="Arrasta para criar match">
              <IconButton size="small">
                <DragIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={0.5} mt={1}>
          {isDebit ? (
            <DebitIcon fontSize="small" color="error" />
          ) : (
            <CreditIcon fontSize="small" color="success" />
          )}
          <Typography variant="caption" color="text.secondary">
            {isDebit ? "Débito" : "Crédito"}
          </Typography>
          <Chip
            label={dayjs(transaction.data_movimento).format("DD/MM/YYYY")}
            size="small"
            variant="outlined"
          />
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1.5 }}
          title={transaction.descricao}
        >
          {transaction.descricao.length > 60
            ? `${transaction.descricao.substring(0, 60)}...`
            : transaction.descricao}
        </Typography>

        {transaction.categoria && (
          <Box mt={1}>
            <Chip label={transaction.categoria} size="small" color="default" />
          </Box>
        )}

        <Typography
          variant="caption"
          display="block"
          color="text.secondary"
          sx={{ mt: 1 }}
        >
          Saldo: €{Number(transaction.saldo).toFixed(2)}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default TransactionCard;
